import React, { useState, useEffect, useRef } from "react";
import {
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Box,
  Input,
  useToast,
  Select,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  Badge,
  Switch,
  Spinner,
  HStack,
  SimpleGrid,
  FormErrorMessage
} from "@chakra-ui/react";
import { MdDelete, MdEdit, MdDone, MdCancel, MdAdd } from "react-icons/md";
import Card from "components/card/Card";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";

const ListServiceTypes = () => {
  const { t } = useTranslation();

  // State to hold the list of service types
  const [serviceTypesList, setServiceTypesList] = useState([]);
  const [filteredServiceTypes, setFilteredServiceTypes] = useState([]);
  const [error, setError] = useState("");
  const [editableServiceType, setEditableServiceType] = useState(null);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const imageInputRef = useRef(null);
  const [imageSize, setImageSize] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [newServiceType, setNewServiceType] = useState({
    name: "",
    description: "",
    translation_key: "",
    service_category_id: "",
    status: "on",
    image: "",
    translations: {
      fr: "",
      it: ""
    }
  });

 
  const [permissions, setPermissions] = useState({ canList: true, canEdit: true, canDelete: true, canInsert: true });
  const pageId= 13;
   // ✅ Image size validator (max 1MB)
  const validateImageSize = (file) => file.size <= 1024 * 1024;

  // ✅ Handle image upload + base64 + validation
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImageSize(file.size);
      if (!validateImageSize(file)) {
        setFieldErrors((prev) => ({
          ...prev,
          image: t("Image must be less than 1MB"),
        }));
        return;
      }
      setFieldErrors((prev) => ({ ...prev, image: undefined }));
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        setNewServiceType({ ...newServiceType, image: reader.result });
      };
    } else {
      setImageSize(null);
    }
  };
  // Function to handle image change in edit mode
  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditableServiceType({
          ...editableServiceType,
          image: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };
  // On mount, decode token and set updateUser and userRole.
  useEffect(() => {
    try {
      const role = JSON.parse(sessionStorage.getItem("permissions") || "{}");
      if (!role.permissions) {        toast({
          title: t("No permissions found"),
          description: t("You don't have the required access."),
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
        return;
      }

      const permission = role.permissions.find((p) => p.page_id === pageId);
      if (permission) {
        setPermissions({
          canList: permission.read_all_access || false,
          canEdit: permission.update_access || false,
          canDelete: permission.delete_access || false,
          canInsert: permission.insert_access || false,
        });
      }    } catch (error) {
      toast({
        title: t("Error loading permissions"),
        description: error.message || t("Something went wrong while fetching permissions."),
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }
  }, [toast, t]);

  // Fetch service categories for the dropdown
  useEffect(() => {
    const fetchServiceCategories = async () => {
      try {
        const response = await fetchWithToken(
          `${process.env.REACT_APP_API_URL}/service-categories?page_id=${pageId}`,
          "GET"
        );
        setServiceCategories(response.data);
      } catch (error) {
        console.error("Error fetching service categories:", error);
        toast({
          title: t("Error fetching service categories"),
          description: error.message || t("Failed to load service categories."),
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      }
    };

    fetchServiceCategories();
  }, [toast, t]);
  // Fetch all service types from the endpoint.
  useEffect(() => {
    const fetchServiceTypes = async () => {
      try {
        if (!selectedCategoryId) {
          setServiceTypesList([]);
          setFilteredServiceTypes([]);
          setError("");
          return;
        }

        // Updated to use the new API endpoint for getting service types by category ID
        const response = await fetchWithToken(
          `${process.env.REACT_APP_API_URL}/services/service-types?service_category_id=${selectedCategoryId}&page_id=${pageId}`,
          "GET"
        );
        setServiceTypesList(response.data);
        setFilteredServiceTypes(response.data);
        setError(""); // Clear any previous errors
      } catch (error) {
        console.error("Fetch error:", error);
        setError(error.message);
        toast({
          title: t("Error fetching sub-services"),
          description: error.message || t("Failed to load sub-services for this category."),
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      }    };

    fetchServiceTypes();
  }, [selectedCategoryId, toast, t]);  

  // Update newServiceType when selectedCategoryId changes
  useEffect(() => {
    setNewServiceType(prev => ({
      ...prev,
      service_category_id: selectedCategoryId || ""
    }));
  }, [selectedCategoryId]);

  // Delete a service type
  const handleDeleteServiceType = async (serviceTypeId) => {
    try {
      await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/service-types/service-type/delete?page_id=${pageId}`,
        "DELETE",
        { id: serviceTypeId }
      );
      setServiceTypesList((prev) =>
        prev.filter((serviceType) => serviceType.id !== serviceTypeId)
      );
      setFilteredServiceTypes((prev) =>
        prev.filter((serviceType) => serviceType.id !== serviceTypeId)
      );
        toast({
        title: t("Service Type deleted successfully"),
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {      console.error("Error deleting service type:", error);
      setError(t("Failed to delete service type"));
      
      toast({
        title: t("Delete failed"),
        description: error.message || t("Failed to delete service type."),
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  // Enable edit mode for a service type
  const handleEditServiceType = (serviceType) => {
    setEditableServiceType(serviceType);
  };  // Save updated service type using the new PATCH endpoint
 const handleSaveServiceType = async () => {
  try {
    // Construct the data object
    let data = {
      id: editableServiceType.id,
      name: editableServiceType.name,
      description: editableServiceType.description,
      translation_key: editableServiceType.translation_key || "",
      service_category_id: editableServiceType.service_category_id || selectedCategoryId,
      status: editableServiceType.status || "on",
      translations: editableServiceType.translations || {},
    };

    // Only include the image if it has been updated
    if (editableServiceType.image) {
      data.image = editableServiceType.image;
    }

    await fetchWithToken(
      `${process.env.REACT_APP_API_URL}/service-types/service-type/update?page_id=${pageId}`,
      "PATCH",
      data
    );

    setServiceTypesList((prev) =>
      prev.map((st) =>
        st.id === editableServiceType.id
          ? { ...st, ...editableServiceType }
          : st
      )
    );
    setFilteredServiceTypes((prev) =>
      prev.map((st) =>
        st.id === editableServiceType.id
          ? { ...st, ...editableServiceType }
          : st
      )
    );

    // Reset file input
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }

    setEditableServiceType(null);

    // Show success message
    toast({
      title: t("Service type updated"),
      description: "The service type has been updated successfully.",
      status: "success",
      duration: 3000,
      isClosable: true,
      position: "top-right",
    });
  } catch (error) {
    console.error("Error updating service type:", error);
    setError(error.message);

    // Show error message
    toast({
      title: t("Update failed"),
      description: error.message || t("Failed to update service type."),
      status: "error",
      duration: 3000,
      isClosable: true,
      position: "top-right",
    });
  }
};

  // Handle create service type
  const handleCreateServiceType = async () => {
    try {
      setLoading(true);
        if (!newServiceType.name || !newServiceType.description || !newServiceType.translation_key || !selectedCategoryId) {
        toast({          title: t("Missing required fields"),
          description: t("Please fill in all required fields (name, description, translation key) and select a service category."),
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
        return;
      }

      const data = {
        name: newServiceType.name,
        description: newServiceType.description,
        translation_key: newServiceType.translation_key,
        service_category_id: selectedCategoryId,
        status: newServiceType.status || "on",
        image: newServiceType.image,
        translations: newServiceType.translations
      };

      await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/service-types?page_id=${pageId}`,
        "POST",
        data
      );

      toast({
        title: t("Service Type created successfully"),
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });      // Reset form
      setNewServiceType({
        name: "",
        description: "",
        translation_key: "",
        service_category_id: selectedCategoryId || "",
        status: "on",
        image: "",
        translations: {
          fr: "",
          it: ""
        }
      });

      // Refresh the list
      const response = await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/services/service-types?service_category_id=${selectedCategoryId}&page_id=${pageId}`,
        "GET"
      );
      setServiceTypesList(response.data);
      setFilteredServiceTypes(response.data);

      setShowCreateModal(false);

    } catch (error) {
      toast({        title: t("Failed to create service type"),
        description: error.message || t("Something went wrong while creating the service type."),
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter the service types by search query
  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    const filtered = serviceTypesList.filter((serviceType) =>
      serviceType.name.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredServiceTypes(filtered);
  };  return (
    <Card
      direction="column"
      w="100%"
      px="0px"
      overflowX={{ base: "scroll", lg: "hidden" }}
      boxShadow="lg"
    >
      <Flex
        px={{ base: "10px", md: "25px" }}
        justify="space-between"
        mb="20px"
        align="center"
        flexWrap="wrap"
      >
        <Text
          color="black"
          fontSize={{ base: "18px", md: "22px" }}
          fontWeight="700"
          lineHeight="100%"
        >
          {t("Service Types List")}
        </Text>
        {error && <Text color="red.500">{error}</Text>}
      </Flex>
      
      {/* Service Category Selector */}
      <Flex
        px={{ base: "10px", md: "25px" }}
        mb="20px"
        align="center"
        gap={4}
        flexWrap="wrap"
      >
        <Box minW="200px">
          <Text mb={1} fontWeight="500" fontSize="14px" color="gray.600">
            {t("Select Service Category")}
          </Text>
          <Select
            placeholder={t("Choose a service category...")}
            value={selectedCategoryId}
            onChange={(e) => setSelectedCategoryId(e.target.value)}
            bg="white"
            borderRadius="8px"
            border="1px solid"
            borderColor="gray.300"
            _focus={{
              borderColor: "blue.500",
              boxShadow: "0 0 0 1px blue.500",
            }}
          >
            {serviceCategories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Select>
        </Box>
        <Box mt={4}>
          <Flex align="center" gap={3}>
          <Input
            placeholder={t("Search service types...")}
            value={searchQuery}
            mt={2}
            onChange={handleSearch}
            size="md"
            w={{ base: "100%", md: "200px" }}
          />
          {selectedCategoryId && (
            <Button
              leftIcon={<MdAdd />}
              mt={2}
              colorScheme="green"
              size="sm"
              onClick={() => setShowCreateModal(true)}
              isDisabled={!permissions.canInsert}
            >
              {t("Add Service Type")}
            </Button>
          )}
          </Flex>
        </Box>
      </Flex>
      {!selectedCategoryId ? (
        <Box p="20px" textAlign="center">
          <Text fontSize="lg" color="gray.500">
            {t("Please select a service category to view its sub-services.")}
          </Text>
        </Box>
      ) : (
    <Box
  overflowX="auto"
  overflowY="auto"
  maxHeight={{ base: "auto", md: "400px" }}
  w="100%"
>
  <Table
    variant="simple"
    color="gray.500"
    mb="24px"
    minWidth="900px"
    size={{ base: "sm", md: "md" }}
  >
     <Thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                  <Tr bg='rgb(69, 50, 240)' textColor="white" style={{ position: 'sticky', top: 0, zIndex: 2 }}>
        <Th minWidth="60px" color={"white"}>{t("Index")}</Th>
        <Th minWidth="180px" color={"white"}>{t("Name")}</Th>
        <Th minWidth="220px" color={"white"}>{t("Description")}</Th>
        <Th minWidth="180px" color={"white"}>{t("Translation Key")}</Th>
        <Th minWidth="120px" color={"white"}>{t("Image")}</Th>
        <Th minWidth="220px" color={"white"}>{t("Translations")}</Th>
        <Th minWidth="120px" color={"white"}>{t("Status")}</Th>
        <Th minWidth="160px" color={"white"}>{t("Actions")}</Th>
      </Tr>
    </Thead>
    <Tbody>
      {filteredServiceTypes.map((serviceType, index) => (
        <Tr key={serviceType.id}>
          <Td>{index + 1}</Td>
          <Td>
            {editableServiceType &&
            editableServiceType.id === serviceType.id ? (
              <Input
                value={editableServiceType.name}
                onChange={(e) =>
                  setEditableServiceType({
                    ...editableServiceType,
                    name: e.target.value,
                  })
                }
                width="100%"
                minWidth="150px"
                fontSize={{ base: "sm", md: "md" }}
              />
            ) : (
              <Text
                isTruncated
                maxWidth="180px"
                fontSize={{ base: "sm", md: "md" }}
              >
                {serviceType.name}
              </Text>
            )}
          </Td>
          <Td>
            {editableServiceType &&
            editableServiceType.id === serviceType.id ? (
              <Textarea
                resize="vertical"
                rows={2}
                value={editableServiceType.description}
                onChange={(e) =>
                  setEditableServiceType({
                    ...editableServiceType,
                    description: e.target.value,
                  })
                }
                width="100%"
                minWidth="200px"
                fontSize={{ base: "sm", md: "md" }}
              />
            ) : (
              <Text
                // Remove isTruncated so full description is visible
                maxWidth="220px"
                fontSize={{ base: "sm", md: "md" }}
                whiteSpace="pre-wrap"
                wordBreak="break-word"
              >
                {serviceType.description}
              </Text>
            )}
          </Td>
          <Td>
            {editableServiceType &&
            editableServiceType.id === serviceType.id ? (
              <Textarea
                resize="vertical"
                rows={2}
                value={editableServiceType.translation_key || ""}
                onChange={(e) =>
                  setEditableServiceType({
                    ...editableServiceType,
                    translation_key: e.target.value,
                  })
                }
                width="100%"
                minWidth="150px"
                fontSize={{ base: "sm", md: "md" }}
                placeholder={t("Translation key")}
              />
            ) : (
              <Text
                // Remove isTruncated so full key is visible
                maxWidth="180px"
                fontSize={{ base: "sm", md: "md" }}
                whiteSpace="pre-wrap"
                wordBreak="break-word"
              >
                {serviceType.translation_key || t("N/A")}
              </Text>
            )}
          </Td>
          
          <Td>
            {editableServiceType &&
            editableServiceType.id === serviceType.id ? (
              <Flex direction="column" align="center">
                {editableServiceType.image && (
                  <Image
                    src={editableServiceType.image}
                    alt={editableServiceType.name}
                    boxSize="50px"
                    objectFit="cover"
                    borderRadius="md"
                    mb={2}
                  />
                )}
                <Flex align="center">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    ref={imageInputRef}
                    size="sm"
                    width="auto"
                    py={1}
                  />
                </Flex>
              </Flex>
            ) : serviceType.image ? (
              <Image
                src={serviceType.image}
                alt={serviceType.name}
                boxSize="50px"
                objectFit="cover"
                borderRadius="md"
              />
            ) : (
              t("No image")
            )}
          </Td>
          <Td>
            {editableServiceType && editableServiceType.id === serviceType.id ? (
              <Flex direction="column" gap={2}>
                <Flex align="center">
                  <Text fontWeight="bold" minWidth="55px" mr={2}>
                    Key:
                  </Text>
                  <Input
                    borderColor="gray.300"
                    value={editableServiceType.translation_key || ""}
                    onChange={(e) =>
                      setEditableServiceType({
                        ...editableServiceType,
                        translation_key: e.target.value,
                      })
                    }
                    size="sm"
                  />
                </Flex>
                <Flex align="center">
                  <Text fontWeight="bold" minWidth="55px" mr={2}>
                    IT:
                  </Text>
                  <Input
                    borderColor="gray.300"
                    value={editableServiceType.translations?.it || ""}
                    onChange={(e) =>
                      setEditableServiceType({
                        ...editableServiceType,
                        translations: {
                          ...editableServiceType.translations,
                          it: e.target.value,
                        },
                      })
                    }
                    size="sm"
                  />
                </Flex>
                <Flex align="center">
                  <Text fontWeight="bold" minWidth="55px" mr={2}>
                    FR:
                  </Text>
                  <Input
                    borderColor="gray.300"
                    value={editableServiceType.translations?.fr || ""}
                    onChange={(e) =>
                      setEditableServiceType({
                        ...editableServiceType,
                        translations: {
                          ...editableServiceType.translations,
                          fr: e.target.value,
                        },
                      })
                    }
                    size="sm"
                  />
                </Flex>
              </Flex>
            ) : (
              <Flex direction="column">
                {serviceType.translation_key && (
                  <Text fontSize="sm" fontWeight="medium" whiteSpace="pre-wrap" wordBreak="break-word">
                    Key: {serviceType.translation_key}
                  </Text>
                )}
                {serviceType.translations?.it && (
                  <Text fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-word">
                    IT: {serviceType.translations.it}
                  </Text>
                )}
                {serviceType.translations?.fr && (
                  <Text fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-word">
                    FR: {serviceType.translations.fr}
                  </Text>
                )}
                {!serviceType.translation_key &&
                  !serviceType.translations?.it &&
                  !serviceType.translations?.fr && (
                    <Text fontSize="sm" color="gray.500">
                      No translations
                    </Text>
                  )}
              </Flex>
            )}
          </Td>
       <Td>
  {editableServiceType && editableServiceType.id === serviceType.id ? (
    <Flex align="center">
      <Switch
        isChecked={editableServiceType.status === "on"} // Map "on" to true and "off" to false
        onChange={(e) =>
          setEditableServiceType((prev) => ({
            ...prev,
            status: e.target.checked ? "on" : "off", // Map true to "on" and false to "off"
          }))
        }
        colorScheme="cyan"
        mr={2}
      />
      <Badge
        colorScheme={editableServiceType.status === "on" ? "green" : "red"}
        variant="solid"
        px={2}
        py={1}
        borderRadius="md"
        fontSize="0.85em"
      >
        {editableServiceType.status === "on" ? t("Active") : t("Inactive")}
      </Badge>
    </Flex>
  ) : (
    <Badge
      colorScheme={serviceType.status === "on" ? "green" : "red"}
      variant="solid"
      px={2}
      py={1}
      borderRadius="md"
      fontSize="0.85em"
    >
      {serviceType.status === "on" ? t("Active") : t("Inactive")}
    </Badge>
  )}
</Td>
          <Td>
            {editableServiceType &&
            editableServiceType.id === serviceType.id ? (
              <Flex>
                <Button
                  colorScheme="green"
                  size="sm"
                  onClick={handleSaveServiceType}
                  borderRadius="full"
                  boxShadow="md"
                  _hover={{ bg: "green.600", transform: "scale(1.05)" }}
                  _active={{ bg: "green.700", transform: "scale(0.95)" }}
                  mr={2}
                  color={"white"}
                  leftIcon={<MdDone />}
                >
                  {t("Save")}
                </Button>
                <Button
                  colorScheme="red"
                  size="sm"
                  onClick={() => setEditableServiceType(null)}
                  borderRadius="full"
                  boxShadow="md"
                  color={"white"}
                  _hover={{ bg: "red.600", transform: "scale(1.05)" }}
                  _active={{ bg: "red.700", transform: "scale(0.95)" }}
                  leftIcon={<MdCancel />}
                >
                  {t("Cancel")}
                </Button>
              </Flex>
            ) : (
              <Flex>
                <Button
                  colorScheme="cyan"
                  size="sm"
                  onClick={() => handleEditServiceType(serviceType)}
                  borderRadius="full"
                  isDisabled={!permissions.canEdit}
                  boxShadow="md"
                  color={"white"}
                  _hover={{ bg: "cyan.600", transform: "scale(1.05)" }}
                  _active={{ bg: "cyan.700", transform: "scale(0.95)" }}
                  leftIcon={<MdEdit />}
                >
                  {t("Edit")}
                </Button>
                {/* <Button
                  colorScheme="red"
                  isDisabled={!permissions.canDelete}
                  size="sm"
                  ml={2}
                  onClick={() => {
                    if (
                      window.confirm(
                        "Are you sure you want to delete this service type?"
                      )
                    ) {
                      handleDeleteServiceType(serviceType.id);
                    }
                  }}
                  borderRadius="full"
                  boxShadow="md"
                  color={"white"}
                  _hover={{ bg: "red.600", transform: "scale(1.05)" }}
                  _active={{ bg: "red.700", transform: "scale(0.95)" }}
                  leftIcon={<MdDelete />}
                >
                  {t("Delete")}
                </Button> */}
              </Flex>
            )}
          </Td>
        </Tr>
      ))}
    </Tbody>
  </Table>
</Box>
      )}

      {/* Create Service Type Modal */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} size="2xl">
      <ModalOverlay />
      <ModalContent p={2}>
        <ModalHeader color="blue.600">{t("Create Service Type")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {/* Name */}
            <FormControl isRequired>
              <FormLabel color="blue.600">{t("Service Type Name")}</FormLabel>
              <Input
                placeholder={t("Service Type Name")}
                value={newServiceType.name}
                onChange={(e) =>
                  setNewServiceType({ ...newServiceType, name: e.target.value })
                }
              />
            </FormControl>

            {/* Description */}
            <FormControl isRequired>
              <FormLabel color="blue.600">{t("Description")}</FormLabel>
              <Textarea
                placeholder={t("Description")}
                value={newServiceType.description}
                onChange={(e) =>
                  setNewServiceType({ ...newServiceType, description: e.target.value })
                }
                minH="40px"
                size="sm"
                borderRadius="8px"
              />
            </FormControl>

            {/* Translation Key */}
            <FormControl isRequired>
              <FormLabel color="blue.600">{t("Translation Key")}</FormLabel>
              <Input
                placeholder={t("Translation Key")}
                value={newServiceType.translation_key}
                onChange={(e) =>
                  setNewServiceType({ ...newServiceType, translation_key: e.target.value })
                }
              />
            </FormControl>

            {/* French Translation */}
            <FormControl>
              <FormLabel color="blue.600">
                {t("Translation")}{" "}
                <Text as="span" fontWeight="bold">
                  {t("(French)")}
                </Text>
              </FormLabel>
              <Input
                placeholder={t("Translation (French)")}
                value={newServiceType.translations.fr}
                onChange={(e) =>
                  setNewServiceType({
                    ...newServiceType,
                    translations: {
                      ...newServiceType.translations,
                      fr: e.target.value,
                    },
                  })
                }
              />
            </FormControl>

            {/* Italian Translation */}
            <FormControl>
              <FormLabel color="blue.600">
                {t("Translation")}{" "}
                <Text as="span" fontWeight="bold">
                  {t("(Italian)")}
                </Text>
              </FormLabel>
              <Input
                placeholder={t("Translation (Italian)")}
                value={newServiceType.translations.it}
                onChange={(e) =>
                  setNewServiceType({
                    ...newServiceType,
                    translations: {
                      ...newServiceType.translations,
                      it: e.target.value,
                    },
                  })
                }
              />
            </FormControl>

           

            {/* Service Category */}
            <FormControl isRequired>
              <FormLabel color="blue.600">{t("Select Service Category")}</FormLabel>
              <Select
                placeholder={t("Select Service Category")}
                value={newServiceType.service_category_id}
                onChange={(e) =>
                  setNewServiceType({
                    ...newServiceType,
                    service_category_id: e.target.value,
                  })
                }
              >
                {serviceCategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </Select>
            </FormControl>

                 {/* Image */}
            <FormControl isInvalid={!!fieldErrors.image}>
              <FormLabel color="blue.600">{t("Image")}</FormLabel>
              <Input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                ref={imageInputRef}
              />
               {/* Always show the helper text */}
              <Text fontSize="sm" color="gray.500" mt={2}>
                {t("Recommended size: Max 1 MB (≈1024 KB), up to 1024×1024 pixels")}
              </Text>
              {imageSize !== null && (
                <Text
                  fontSize="xs"
                  color={imageSize > 1024 * 1024 ? "red.500" : "gray.500"}
                  mt={1}
                >
                  {t("Selected file size")}: {(imageSize / 1024).toFixed(1)} KB
                  {imageSize > 1024 * 1024 && ` (${t("Too large")})`}
                </Text>
              )}
              <FormErrorMessage>{fieldErrors.image}</FormErrorMessage>
            </FormControl>
            {/* Status */}
            <FormControl isRequired>
              <FormLabel color="blue.600">{t("Status")}</FormLabel>
              <HStack spacing={4}>
                <Switch
                  isChecked={newServiceType.status === "on"}
                  onChange={(e) =>
                    setNewServiceType({
                      ...newServiceType,
                      status: e.target.checked ? "on" : "off",
                    })
                  }
                  colorScheme="cyan"
                />
                <Badge
                  colorScheme={newServiceType.status === "on" ? "green" : "red"}
                  variant="solid"
                  px={3}
                  py={1}
                  borderRadius="md"
                >
                  {newServiceType.status === "on" ? t("Active") : t("Inactive")}
                </Badge>
              </HStack>
            </FormControl>
          </SimpleGrid>

          {/* Image preview */}
          {newServiceType.image && (
            <Box mt={6}>
              <Text fontWeight="bold" mb={2}>
                {t("Selected Image")}
              </Text>
              <Box textAlign="center">
                <img
                  src={newServiceType.image}
                  alt="Preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "120px",
                    borderRadius: "8px",
                  }}
                />
              </Box>
            </Box>
          )}
        </ModalBody>

        <ModalFooter>
         
          <Button
            colorScheme="cyan"
            onClick={handleCreateServiceType}
            isLoading={loading}
            color="white"
            borderRadius="16px"
            px={6}
          >
            {loading ? <Spinner size="sm" /> : t("Create Service Type")}
          </Button>
          
        </ModalFooter>
      </ModalContent>
    </Modal>
    </Card>
  );
};

export default ListServiceTypes;
